import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { mediaUrl } from "@/lib/media";

/**
 * Modal image picker. Lists all uploaded media; allows search + on-the-fly upload.
 * `onPick(path)` receives the relative URL of the chosen size (medium by default).
 */
export default function MediaPicker({ open, onClose, onPick, size = "medium" }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async (query = "") => {
    const r = await api.get("/admin/media", { params: query ? { q: query } : {} });
    setItems(r.data || []);
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  if (!open) return null;

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/media", fd);
      toast.success("Uploaded.");
      setItems((prev) => [r.data, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="media-picker" className="fixed inset-0 z-50 bg-jade/40 flex items-stretch justify-center px-4 py-10">
      <div className="bg-ivory w-full max-w-5xl flex flex-col">
        <div className="flex items-center justify-between border-b border-mist px-6 py-4">
          <div>
            <p className="eyebrow">Media library</p>
            <p className="font-serif text-2xl text-jade mt-1">Choose an image</p>
          </div>
          <button onClick={onClose} className="text-ink/60 hover:text-copper" data-testid="media-picker-close"><X size={20} /></button>
        </div>

        <div className="px-6 py-4 border-b border-mist flex items-center gap-4">
          <div className="flex items-center gap-2 flex-1 border-b border-mist pb-2">
            <Search size={14} className="text-ink/50" />
            <input
              data-testid="media-picker-search"
              type="text"
              placeholder="Search by name, alt text, caption, credit, notes…"
              value={q}
              onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
              className="flex-1 bg-transparent outline-none text-[14px]"
            />
          </div>
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={onUpload} className="hidden" data-testid="media-picker-file" />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid="media-picker-upload"
            className="btn-primary text-[12px] py-2 px-4 inline-flex items-center gap-2 disabled:opacity-40"
          >
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {items.length === 0 ? (
            <p className="text-ink/60 text-sm italic">No images yet. Upload one to get started.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {items.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`media-pick-${m.id}`}
                  onClick={() => { onPick(m.files?.[size] || m.files?.medium || m.files?.original); onClose(); }}
                  className="group text-left border border-transparent hover:border-jade transition-colors"
                >
                  <div className="aspect-square bg-mist/40 overflow-hidden">
                    <img src={mediaUrl(m.files?.thumb || m.files?.medium)} alt={m.alt_text || m.original_name} className="w-full h-full object-cover" />
                  </div>
                  <p className="mt-2 text-[12px] text-ink/75 truncate">{m.alt_text || m.original_name}</p>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-ink/40">{m.width}×{m.height}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
